const CANCELLED_STATUSES = new Set(['cancelled', 'canceled']);

export const toDate = (value) => {
  if (!value) return null;
  if (typeof value.toDate === 'function') return value.toDate();
  if (value instanceof Date) return value;
  if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
    return new Date(`${value}T12:00:00`);
  }
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

export const toDateKey = (value) => {
  const date = toDate(value);
  if (!date) return '';
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

export const getParticipants = (reservation) => Math.max(
  1,
  Number(reservation?.participants || reservation?.guests || 1) || 1
);

export const isCancelled = (reservation) => CANCELLED_STATUSES.has(reservation?.status);

export const isCheckedIn = (reservation) => reservation?.checkInStatus === 'checked_in';

export const getServiceCapacity = (service) => {
  if (service?.isPousada || service?.type === 'inn' || service?.type === 'pousada') {
    return (service?.rooms || []).reduce((total, room) => total + (Number(room.capacity) || 0), 0);
  }
  return Number(service?.capacity) || 0;
};

export const summarizeServiceDay = (service, reservations, dateKey) => {
  const dayReservations = reservations.filter((reservation) => (
    reservation.serviceId === service.id && toDateKey(reservation.date) === dateKey
  ));
  const validReservations = dayReservations.filter((reservation) => !isCancelled(reservation));
  const checkedReservations = validReservations.filter(isCheckedIn);
  const expectedPeople = validReservations.reduce((sum, item) => sum + getParticipants(item), 0);
  const checkedPeople = checkedReservations.reduce((sum, item) => sum + getParticipants(item), 0);
  const capacity = getServiceCapacity(service);

  return {
    reservations: dayReservations,
    validReservations,
    reservationCount: validReservations.length,
    checkedReservationCount: checkedReservations.length,
    expectedPeople,
    checkedPeople,
    missingPeople: Math.max(0, expectedPeople - checkedPeople),
    missingReservationCount: Math.max(0, validReservations.length - checkedReservations.length),
    cancelledCount: dayReservations.length - validReservations.length,
    capacity,
    availablePeople: capacity > 0 ? Math.max(0, capacity - expectedPeople) : null,
    occupancyRate: capacity > 0 ? (expectedPeople / capacity) * 100 : 0,
    checkInRate: expectedPeople > 0 ? (checkedPeople / expectedPeople) * 100 : 0
  };
};

export const summarizePeriod = (reservations, services, startKey, endKey, todayKey = toDateKey(new Date())) => {
  const periodReservations = reservations.filter((reservation) => {
    const key = toDateKey(reservation.date);
    return key && key >= startKey && key <= endKey;
  });
  const validReservations = periodReservations.filter((reservation) => !isCancelled(reservation));
  const attendanceSchedule = validReservations.filter((reservation) => toDateKey(reservation.date) <= todayKey);
  const completedSchedule = validReservations.filter((reservation) => toDateKey(reservation.date) < todayKey);
  const checkedReservations = attendanceSchedule.filter(isCheckedIn);
  const noShows = completedSchedule.filter((reservation) => !isCheckedIn(reservation));
  const expectedPeople = validReservations.reduce((sum, item) => sum + getParticipants(item), 0);
  const checkedPeople = validReservations.filter(isCheckedIn).reduce((sum, item) => sum + getParticipants(item), 0);
  const cancelledCount = periodReservations.length - validReservations.length;
  const decisionBase = periodReservations.length;
  const revenue = validReservations.reduce((sum, item) => {
    return ['confirmed', 'completed'].includes(item.status) ? sum + (Number(item.totalAmount) || 0) : sum;
  }, 0);

  const byService = services.map((service) => {
    const serviceReservations = periodReservations.filter((item) => item.serviceId === service.id);
    const serviceValid = serviceReservations.filter((item) => !isCancelled(item));
    const serviceCompleted = serviceValid.filter((item) => toDateKey(item.date) <= todayKey);
    const serviceChecked = serviceCompleted.filter(isCheckedIn);
    const people = serviceValid.reduce((sum, item) => sum + getParticipants(item), 0);
    const checked = serviceValid.filter(isCheckedIn).reduce((sum, item) => sum + getParticipants(item), 0);
    return {
      id: service.id,
      name: service.name || 'Serviço',
      reservations: serviceValid.length,
      people,
      checkedPeople: checked,
      checkInRate: serviceCompleted.length > 0 ? (serviceChecked.length / serviceCompleted.length) * 100 : 0,
      cancellations: serviceReservations.length - serviceValid.length,
      revenue: serviceValid.reduce((sum, item) => (
        ['confirmed', 'completed'].includes(item.status) ? sum + (Number(item.totalAmount) || 0) : sum
      ), 0)
    };
  }).filter((item) => item.reservations > 0 || item.cancellations > 0)
    .sort((a, b) => b.revenue - a.revenue || b.people - a.people);

  return {
    reservations: periodReservations,
    reservationCount: validReservations.length,
    expectedPeople,
    checkedPeople,
    pendingCheckIns: Math.max(0, expectedPeople - checkedPeople),
    checkedReservationCount: checkedReservations.length,
    noShowCount: noShows.length,
    cancelledCount,
    checkInRate: attendanceSchedule.length > 0 ? (checkedReservations.length / attendanceSchedule.length) * 100 : 0,
    dropoutRate: decisionBase > 0 ? ((cancelledCount + noShows.length) / decisionBase) * 100 : 0,
    revenue,
    byService
  };
};
